/**
 * ALOHA PLATFORM - Admin Webhook Testing
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, RefreshCw, Webhook, DollarSign } from 'lucide-react';
import RealisticGalaxyBackground from '../components/RealisticGalaxyBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminWebhooks = () => {
  const { user, isAuthenticated, token } = useAuth();
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stripeConfig, setStripeConfig] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user?.email === 'malcolmlee3@gmail.com') {
      fetchWebhookLogs();
      fetchStripeConfig();
    }
  }, [isAuthenticated, user]);

  const fetchWebhookLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/webhook-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhookLogs(response.data.recent_updates || []);
    } catch (error) {
      console.error('Failed to fetch webhook logs:', error);
    }
  };

  const fetchStripeConfig = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/config`);
      setStripeConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe config:', error);
    }
  };

  const testWebhook = async () => {
    setLoading(true);
    setTestResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/test-webhook`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setTestResult({
        success: true,
        ...response.data
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data?.detail || 'Webhook test failed'
      });
    } finally {
      setLoading(false);
      fetchWebhookLogs();
    }
  };

  const createTestSubscription = async (tier) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/upgrade`,
        { tier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.open(response.data.checkout_url, '_blank');
      }
    } catch (error) {
      alert(`Error: ${error.response?.data?.detail || 'Failed to create checkout'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.email !== 'malcolmlee3@gmail.com') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-2xl">Access Denied - Admin Only</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <RealisticGalaxyBackground />
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-600/80 to-orange-600/80 backdrop-blur-xl border-2 border-yellow-400 shadow-2xl rounded-2xl p-8 mb-8">
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <Webhook className="w-10 h-10" />
              Webhook Testing & Subscription Admin
            </h1>
            <p className="text-yellow-100">Malcolm KH Lee III - Admin Control Panel</p>
          </div>

          {/* Stripe Configuration */}
          {stripeConfig && (
            <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 mb-8 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-4">Stripe Configuration</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Publishable Key</p>
                  <p className="text-white font-mono text-xs break-all">
                    {stripeConfig.publishable_key?.substring(0, 20)}...
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Status</p>
                  <p className="text-green-400 font-semibold">✓ Configured</p>
                </div>
              </div>
            </div>
          )}

          {/* Webhook Test Section */}
          <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-4">Test Webhook Integration</h2>
            
            <button
              onClick={testWebhook}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-4 rounded-xl font-semibold transition transform hover:scale-105 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Webhook className="w-5 h-5" />
                  Simulate checkout.session.completed Event
                </>
              )}
            </button>

            {testResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                testResult.success 
                  ? 'bg-green-500/20 border border-green-500' 
                  : 'bg-red-500/20 border border-red-500'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span className="text-white font-semibold">
                    {testResult.success ? 'Webhook Test Successful!' : 'Webhook Test Failed'}
                  </span>
                </div>
                <pre className="text-white text-sm bg-black/50 p-3 rounded overflow-auto">
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Test Subscription Creation */}
          <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-4">Create Test Subscriptions</h2>
            <p className="text-gray-300 mb-4">Create Stripe checkout sessions to test the full payment flow</p>
            
            <div className="grid md:grid-cols-3 gap-4">
              <button
                onClick={() => createTestSubscription('STARTER')}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Test Starter ($29.99)
              </button>
              
              <button
                onClick={() => createTestSubscription('PRO')}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Test Pro ($99.99)
              </button>
              
              <button
                onClick={() => createTestSubscription('ENTERPRISE')}
                disabled={loading}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 px-4 rounded-lg font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Test Enterprise ($499.99)
              </button>
            </div>
            
            <p className="text-yellow-300 text-sm mt-4">
              ⚠️ Note: As malcolmlee3@gmail.com, you won't actually be charged (Lifetime Enterprise)
            </p>
          </div>

          {/* Webhook Logs */}
          <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Recent Webhook Activity</h2>
              <button
                onClick={fetchWebhookLogs}
                className="text-blue-400 hover:text-blue-300 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {webhookLogs.length > 0 ? (
              <div className="space-y-3">
                {webhookLogs.map((log, idx) => (
                  <div key={idx} className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-semibold">{log.email}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        log.tier === 'LIFETIME_ENTERPRISE' 
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500' 
                          : log.tier === 'ENTERPRISE'
                          ? 'bg-yellow-600'
                          : log.tier === 'PRO'
                          ? 'bg-purple-600'
                          : log.tier === 'STARTER'
                          ? 'bg-blue-600'
                          : 'bg-gray-600'
                      } text-white`}>
                        {log.tier}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Updated: {new Date(log.updated).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                No recent webhook activity
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminWebhooks;
